<template>
  <v-card outlined class="modern-card">
    <!-- Premium Header -->
    <v-sheet :color="computedColor" class="pa-6 d-flex align-center">
      <v-avatar size="44" color="white opacity-20" class="mr-4 shadow-sm">
        <v-icon color="white">
          {{ global ? 'fas fa-globe' : (workOrder ? 'fas fa-plus-circle' : (cost.is_supply ? 'fas fa-boxes' : 'fas fa-cogs')) }}
        </v-icon>
      </v-avatar>
      <div>
        <div class="caption white--text opacity-80 font-weight-black text-uppercase tracking-widest mb-1">
           Finanzas y Operaciones
        </div>
        <div class="text-h5 white--text font-weight-black lh-1-2">
          <template v-if="global">Costo Global</template>
          <template v-else-if="workOrder">Costo Ocasional</template>
          <template v-else>
            {{cost.id > 0 ? 'Actualizar' : 'Crear'}} 
            {{cost.is_supply ? 'Material' : 'Proceso'}}
          </template>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn icon color="white" large @click="$emit('close')">
        <v-icon>fas fa-times</v-icon>
      </v-btn>
    </v-sheet>

    <v-card-text class="pa-8 bg-white">
      <ValidationObserver v-slot="{ handleSubmit }">
        <v-form @submit.prevent="handleSubmit(savecost)">
          <v-row dense>
            <!-- Basic Information -->
            <v-col v-if="!(custom || workOrder)" cols="12" class="mb-4">
              <ValidationProvider name="Grupo" rules="required" v-slot="{ errors }">
                <v-select
                  v-model="cost.group"
                  :items="groups"
                  item-text="name"
                  item-value="id"
                  label="Grupo de Costo"
                  placeholder="Seleccione categoría"
                  outlined
                  dense
                  clearable
                  :error-messages="errors"
                  class="modern-input"
                  
                />
              </ValidationProvider>
            </v-col>

            <v-col cols="12" class="mb-4">
              <ValidationProvider name="Nombre" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="cost.name"
                  label="Descripción del Item"
                  outlined
                  dense
                  :error-messages="errors"
                  placeholder="Ej: Flete especial / Embalaje reforzado"
                  class="modern-input"
                  
                />
              </ValidationProvider>
            </v-col>

            <!-- Pricing Row -->
            <v-row dense class="mx-0 w-100">
              <v-col cols="12" :md="!(global || workOrder) ? '6' : '12'" class="mb-4 pr-md-2">
                <ValidationProvider name="Precio" rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model="cost.price"
                    label="Monto Neto / Costo Unitario"
                    type="number"
                    outlined
                    dense
                    prefix="$"
                    :error-messages="errors"
                    class="modern-input font-weight-black"
                  />
                </ValidationProvider>
              </v-col>

              <v-col v-if="!(global || workOrder)" cols="12" md="6" class="mb-4 pl-md-2">
                <ValidationProvider name="Mínimo" rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model="cost.min_price"
                    label="Monto Mínimo"
                    type="number"
                    outlined
                    dense
                    prefix="$"
                    :error-messages="errors"
                    class="modern-input"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <!-- Configuration Row -->
            <v-row dense class="mx-0 w-100" v-if="!workOrder">
              <v-col cols="12" :md="!cost.is_supply ? '6' : '12'" class="mb-4 pr-md-2">
                <ValidationProvider name="Unidades" rules="required" v-slot="{ errors }">
                  <v-select
                    v-model="cost.unit"
                    :items="units"
                    item-text="name"
                    item-value="id"
                    label="Unidad de Medida"
                    outlined
                    dense
                    placeholder="Seleccione Unidad"
                    :loading="loading"
                    :error-messages="errors"
                    class="modern-input"
                    
                  />
                </ValidationProvider>
              </v-col>

              <v-col v-if="!cost.is_supply" cols="12" md="6" class="mb-4 pl-md-2">
                <ValidationProvider name="Área" rules="required" v-slot="{ errors }">
                  <v-select
                    v-model="cost.machine"
                    :items="machines"
                    item-text="name"
                    item-value="id"
                    label="Centro de Costo / Área"
                    outlined
                    dense
                    placeholder="Seleccione Centro"
                    clearable
                    :loading="loadingMachines"
                    :error-messages="errors"
                    class="modern-input"
                   
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <!-- Specialized Fields -->
            <v-col v-if="!cost.is_supply && cost.unit == 2 && !global" cols="12" class="mb-4">
              <ValidationProvider name="Merma" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="cost.merma"
                  label="Merma Estimada (Fija)"
                  type="number"
                  outlined
                  dense
                  :error-messages="errors"
                  class="modern-input"
                  
                />
              </ValidationProvider>
            </v-col>

            <!-- Dimensions -->
            <v-col v-if="cost.is_supply && cost.unit == 2 && !global" cols="12" class="mb-6">
              <v-card outlined class="bg-primary-light border-primary-light pa-4 modern-inner-card">
                <v-checkbox
                  v-model="cost.has_dimension"
                  color="primary"
                  label="Especificar dimensiones físicas"
                  dense
                  hide-details
                  class="mt-0 font-weight-bold"
                />
                <v-expand-transition>
                  <div v-if="cost.has_dimension" class="d-flex mt-4 gap-3">
                    <ValidationProvider name="Ancho" rules="required" v-slot="{ errors }" class="flex-grow-1">
                      <v-text-field
                        v-model="cost.width"
                        label="Ancho (cm)"
                        dense
                        outlined
                        hide-details="auto"
                        :error-messages="errors"
                        background-color="white"
                        class="modern-input"
                      />
                    </ValidationProvider>
                    <ValidationProvider name="Alto" rules="required" v-slot="{ errors }" class="flex-grow-1">
                      <v-text-field
                        v-model="cost.height"
                        label="Alto (cm)"
                        dense
                        outlined
                        hide-details="auto"
                        :error-messages="errors"
                        background-color="white"
                        class="modern-input"
                      />
                    </ValidationProvider>
                  </div>
                </v-expand-transition>
              </v-card>
            </v-col>

            <!-- Inventory Link -->
            <v-col v-if="cost.is_supply && cost.unit == 2 && !custom && !global && !workOrder" cols="12" class="mb-6">
              <v-card outlined class="pa-4 grey lighten-5 modern-inner-card border">
                <v-checkbox
                  :input-value="cost.has_article"
                  color="primary"
                  label="Vincular con Registro de Inventario"
                  dense
                  hide-details
                  class="mt-0 mb-4 font-weight-black"
                  @change="setHasArticle"
                />
                <v-expand-transition>
                  <div v-if="cost.has_article">
                    <article-complete v-model="cost.article" :article_name="cost.article ? cost.article.name : ''" class="mb-4 bg-white"/>
                    
                    <template v-if="cost.article">
                      <v-row dense>
                        <v-col cols="12">
                           <v-select
                            v-model="cost.price_update_option"
                            :items="price_update_options"
                            item-text="name"
                            item-value="value"
                            label="Política de Precios"
                            outlined
                            dense
                            background-color="white"
                            @change="updatePrice"
                            class="modern-input mb-0"
                      
                          />
                        </v-col>
                      </v-row>
                      <v-alert
                        border="left"
                        colored-border
                        color="primary"
                        elevation="0"
                        class="mt-4 mb-0 caption grey lighten-4 grey--text text--darken-2"
                      >
                        <v-icon x-small color="primary" class="mr-2">fas fa-info-circle</v-icon>
                        Se utilizará el registro de inventario para el costeo. No se considera la unidad de compra original.
                      </v-alert>
                    </template>
                  </div>
                </v-expand-transition>
              </v-card>
            </v-col>
          </v-row>

          <v-row dense class="mt-8 mx-0">
            <v-col cols="6" class="pl-0 pr-2">
               <v-btn text large block class="text-none grey--text font-weight-bold" @click="$emit('close')" :disabled="loading">
                Cancelar
              </v-btn>
            </v-col>
            <v-col cols="6" class="pr-0 pl-2">
              <v-btn 
                :loading="loading" 
                :disabled="loadingMachines" 
                color="success" 
                x-large 
                block 
                depressed 
                type="submit"
                class="text-none font-weight-black shadow-sm"
              >
                <v-icon left small>fas fa-check-circle</v-icon>
                Finalizar Registro
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </ValidationObserver>
    </v-card-text>
  </v-card>
</template>

<script>
import { fetchUnits } from '@/api/products'
import { fetchMachines } from '@/api/production'
import notifications from '@/utils/notifications'
import ArticleComplete from '@/components/stocks/ArticleComplete'

export default {
  name: 'CostCreateUpdate',
  components: {
    ArticleComplete,
  },
  props: ['cost', 'groups', 'custom', 'global', 'workOrder', 'color'],
  data: () => ({
    units: [],
    machines: [],
    loading: false,
    loadingMachines: false,
    price_update_options: [
      { name: 'No actualizar Precio', value: 0 },
      { name: 'Sincronizar con Precio Promedio', value: 1 },
      { name: 'Sincronizar con Última Compra', value: 2 },
    ],
  }),
  methods: {
    setHasArticle(val) {
      this.$set(this.cost, 'has_article', val)
    },
    savecost() {
      if (this.cost.has_article && this.cost.article) {
        this.cost.article_id = this.cost.article.id
      }
      this.$emit('close')
      if (this.custom || this.global) {
        this.cost.unit_name = this.cost_unit ? this.cost_unit.name : ''
      }
      this.$emit('save', this.cost)
    },
    updatePrice() {
      if (!this.cost.article) return
      if (this.cost.price_update_option == 1) {
        this.cost.price = this.cost.article.avg
      } else if (this.cost.price_update_option == 2) {
        this.cost.price = this.cost.article.last_buy_price || 0
      }
    },
  },
  computed: {
    computedColor() {
      if (this.color) return this.color
      if (this.global) return 'blue-grey darken-3'
      if (this.workOrder) return 'orange darken-4'
      if (this.cost.is_supply) return 'primary'
      return 'indigo darken-2'
    },
    cost_unit() {
      return this.units.find(u => u.id == this.cost.unit)
    },
  },
  async mounted() {
    this.units = this.$store.getters.getUnits
    if (!this.cost.is_supply) {
      this.loadingMachines = true
      try {
        const data = await fetchMachines()
        this.machines = data
      } catch (err) {
        notifications.showError('Error en carga de áreas: ' + err)
      } finally {
        this.loadingMachines = false
      }
    }
  },
}
</script>

<style scoped>
.modern-card {
  border: 1px solid rgba(0,0,0,0.08) !important;
  border-radius: 12px !important;
}
.modern-inner-card {
  border-radius: 12px !important;
}
.bg-primary-light { background-color: rgba(var(--v-primary-base), 0.05); }
.border-primary-light { border: 1px solid rgba(var(--v-primary-base), 0.1); }
.lh-1-2 { line-height: 1.2; }
.tracking-widest { letter-spacing: 2px; }
.tracking-wider { letter-spacing: 1px; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important; }
.w-100 { width: 100%; }
.bg-white { background-color: white !important; }

.modern-input >>> .v-input__slot {
  border-radius: 8px !important;
}
</style>